import { Box, Text, Badge, Group, Stack } from '@mantine/core';
import { RiskAssessment } from '@medplum/fhirtypes';
import { TimelineItem } from '@medplum/react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { HL7 } from '@consts/systems.consts';

// -----------------------------------------------------------------------------
interface RiskAssessmentCardProps {
  riskAssessment: RiskAssessment;
}

// -----------------------------------------------------------------------------
export const RiskAssessmentCard: FC<RiskAssessmentCardProps> = ({
  riskAssessment,
}) => {
  const { t } = useTranslation(['common', 'resources']);

  // ---------------------------------------------------------------------------
  const riskAssessmentMethod = riskAssessment?.method?.coding?.[0];
  if (
    riskAssessmentMethod?.system !==
      HL7.TERMINOLOGY.CS.RISK_ASSESSMENT.METHOD.BASE ||
    riskAssessmentMethod?.code !==
      HL7.TERMINOLOGY.CS.RISK_ASSESSMENT.METHOD.CODES.ML_PREDICTION
  ) {
    return <div>risk assessment method is currently not supported</div>;
  }

  // ---------------------------------------------------------------------------
  // Group predictions by risk level
  const highRiskPredictions =
    riskAssessment.prediction?.filter(
      (p) => p.probabilityDecimal && p.probabilityDecimal >= 0.5
    ) || [];

  const lowRiskPredictions =
    riskAssessment.prediction?.filter(
      (p) => p.probabilityDecimal && p.probabilityDecimal < 0.5
    ) || [];

  // ---------------------------------------------------------------------------
  return (
    <TimelineItem resource={riskAssessment} padding={true}>
      <Box>
        <Text fw={700}>{`${t('riskAssessment.riskAssessment', {
          ns: 'resources',
        })}:`}</Text>
        {riskAssessment.note?.length &&
          riskAssessment.note.map((note, index) => (
            <Text key={index}>{note.text}</Text>
          ))}

        {(highRiskPredictions.length > 0 || lowRiskPredictions.length > 0) && (
          <Stack gap="xs" mt="md">
            <Text fw={700}>
              {`${t('riskAssessment.predictions', { ns: 'resources' })}:`}
            </Text>

            {highRiskPredictions.length > 0 && (
              <Group gap="xs">
                {highRiskPredictions.map((prediction, index) => (
                  <Badge key={`high-${index}`} color="yellow" variant="filled">
                    {prediction.probabilityDecimal?.toFixed(2)} -{' '}
                    {prediction.outcome?.coding?.[0]?.display ||
                      prediction.outcome?.text ||
                      'Unknown outcome'}
                  </Badge>
                ))}
              </Group>
            )}

            {lowRiskPredictions.length > 0 && (
              <Group gap="xs">
                {lowRiskPredictions.map((prediction, index) => (
                  <Badge key={`low-${index}`} color="green" variant="filled">
                    {prediction.probabilityDecimal?.toFixed(2)} -{' '}
                    {prediction.outcome?.coding?.[0]?.display ||
                      prediction.outcome?.text ||
                      'Unknown outcome'}
                  </Badge>
                ))}
              </Group>
            )}
          </Stack>
        )}
      </Box>
    </TimelineItem>
  );
};
